// CoverageBanner.js — PRD-65 Phase 3 + PRD-83 Phase C.3 + PRD-95 Phase C
//
// Asset-detail page banner(s) about scan coverage gaps. Three variants:
//
//   1. URL-cap variant (PRD-65 Phase 3): the tier's URL cap dropped a
//      meaningful share of the asset's URLs (>50 absolute AND >=20% of
//      total). Enterprise (no cap) never shows.
//
//   2. CMS-coverage variant (PRD-83 Phase C.3): the scan detected
//      WordPress/Drupal/Joomla on a host but produced 0 CMS-specific
//      findings. Two sub-states:
//        - templatesRun > 0 → "CMS scanned, all clear" (positive report)
//        - templatesRun === 0 → "CMS detected, no coverage" (gap, deep-scan recommended)
//
//   3. Port-scan timeout variant (PRD-95 Phase C): IP/CIDR asset's most
//      recent scan completed ipResolution but stageResults.portScanning
//      came back empty — naabu SIGKILL'd mid-flush on large scope. Root
//      fix tracked in PRD-96; this banner just tells the user the open-
//      port list may be incomplete. Only renders when assetType is
//      ip|cidr AND lastPortScanTimeout sticky field is truthy.
//
// All variants can render simultaneously and dismiss independently
// (separate localStorage keys + buckets).

import React, { useEffect, useState, useMemo } from 'react';
import { trackCoverageBannerShown, trackCoverageBannerCta } from '../utils/onboardingAnalytics';
import { getNextMeaningfulTierCap, tierDisplayLabel } from '../utils/tierLadder';
import './CoverageBanner.css';

const MIN_TRUNCATED_ABS = 50;
const MIN_TRUNCATED_FRACTION = 0.20;

function urlCapDismissKey(assetId, fractionBucket) {
    return `driftalarm_coverageBannerDismissed:${assetId || 'global'}:${fractionBucket}`;
}

function cmsDismissKey(assetId, cmsDetected, state) {
    // Bucket on (hosts, state) — if the host set or state flips (e.g., a
    // future scan finds findings), a fresh banner surfaces.
    return `driftalarm_cmsCoverageBannerDismissed:${assetId || 'global'}:${cmsDetected || ''}:${state}`;
}

function portScanTimeoutDismissKey(assetId, lastPortScanTimeout) {
    // Bucket on the timeout timestamp — a fresh timeout re-shows the banner
    // even if the user dismissed the previous one.
    return `driftalarm_portScanTimeoutBannerDismissed:${assetId || 'global'}:${lastPortScanTimeout || ''}`;
}

// ───────────────────────── URL-cap variant ─────────────────────────

function UrlCapBanner({ assetId, urlsTotal, urlsSelected, urlsTruncated, tierCapApplied, currentTier }) {
    const eligible = (
        tierCapApplied != null &&
        typeof urlsTruncated === 'number' &&
        typeof urlsTotal === 'number' &&
        urlsTotal > 0 &&
        urlsTruncated > MIN_TRUNCATED_ABS &&
        (urlsTruncated / urlsTotal) >= MIN_TRUNCATED_FRACTION
    );

    const fraction = eligible ? urlsTruncated / urlsTotal : 0;
    const fractionBucket = eligible ? Math.floor(fraction * 10) : 0;

    const [dismissed, setDismissed] = useState(() => {
        if (!eligible) return false;
        try {
            return localStorage.getItem(urlCapDismissKey(assetId, fractionBucket)) === 'true';
        } catch {
            return false;
        }
    });

    useEffect(() => {
        if (!eligible) return;
        try {
            setDismissed(localStorage.getItem(urlCapDismissKey(assetId, fractionBucket)) === 'true');
        } catch { /* private mode etc. */ }
    }, [eligible, assetId, fractionBucket]);

    const nextTier = useMemo(() => getNextMeaningfulTierCap(currentTier), [currentTier]);

    const visible = eligible && !dismissed;
    const severeBand = fraction > 0.5 ? 'severe' : 'partial';

    useEffect(() => {
        if (visible) {
            trackCoverageBannerShown(
                assetId,
                currentTier || 'unknown',
                nextTier.tier || 'none',
                fractionBucket
            );
        }
    }, [visible, assetId, currentTier, nextTier.tier, fractionBucket]);

    if (!visible) return null;

    const pctText = `${Math.round(fraction * 100)}%`;
    const selectedText = (urlsSelected ?? 0).toLocaleString();
    const totalText = urlsTotal.toLocaleString();
    const truncatedText = urlsTruncated.toLocaleString();
    const capText = tierCapApplied.toLocaleString();
    const currentLabel = tierDisplayLabel(currentTier);

    const headline = severeBand === 'severe'
        ? `Your ${currentLabel} tier scanned ${selectedText} of ${totalText} URLs — ${pctText} (${truncatedText}) skipped`
        : `Your ${currentLabel} tier scanned ${selectedText} of ${totalText} URLs — ${truncatedText} (${pctText}) over the cap`;

    let ctaText = null;
    if (nextTier.cap !== null && nextTier.tier) {
        ctaText = `Upgrade to ${nextTier.label} to scan up to ${nextTier.cap} URLs per scan`;
    } else if (nextTier.tier === 'enterprise') {
        ctaText = `Upgrade to ${nextTier.label} for unlimited URL coverage`;
    }

    const handleDismiss = () => {
        try {
            localStorage.setItem(urlCapDismissKey(assetId, fractionBucket), 'true');
        } catch { /* ignore quota/private-mode failures */ }
        trackCoverageBannerCta('dismiss', assetId, currentTier || 'unknown', nextTier.tier || 'none');
        setDismissed(true);
        const target = document.querySelector('h1, .v3-asset-title');
        if (target) {
            target.setAttribute('tabindex', '-1');
            target.focus({ preventScroll: true });
        }
    };

    const handleCtaClick = () => {
        trackCoverageBannerCta('upgrade_click', assetId, currentTier || 'unknown', nextTier.tier || 'none');
    };

    return (
        <div
            className={`cvb-banner cvb-banner--${severeBand}`}
            role="region"
            aria-labelledby="cvb-title-urlcap"
            data-banner-type="url-cap"
        >
            <button
                type="button"
                className="cvb-dismiss"
                onClick={handleDismiss}
                aria-label="Dismiss URL coverage notice"
                title="Dismiss"
            >
                &times;
            </button>
            <div className="cvb-icon" aria-hidden="true">ⓘ</div>
            <div className="cvb-content">
                <h3 id="cvb-title-urlcap" className="cvb-title">{headline}</h3>
                <p className="cvb-body">
                    Your tier limits scans to {capText} URLs per run, so {truncatedText} URLs
                    discovered on this asset weren&rsquo;t scanned this cycle. The dropped URLs are
                    listed under <strong>Raw Data &rarr; Dropped URLs</strong> if you&rsquo;d like to
                    review them.
                </p>
                {ctaText && (
                    <div className="cvb-actions">
                        <a
                            className="cvb-cta-primary"
                            href="/upgrade"
                            onClick={handleCtaClick}
                        >
                            {ctaText}
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
}

// ───────────────────────── CMS-coverage variant ─────────────────────────

function CmsCoverageBanner({ assetId, cmsDetected, cmsFindings, cmsTemplatesRun }) {
    // Eligible when scan detected at least one CMS-bearing host AND produced
    // 0 CMS-tagged findings. The variant distinguishes "scanned clean" from
    // "no coverage at all" via cmsTemplatesRun.
    const eligible = (
        typeof cmsDetected === 'string' &&
        cmsDetected.length > 0 &&
        (cmsFindings ?? 0) === 0
    );

    const state = (cmsTemplatesRun ?? 0) > 0 ? 'clean' : 'gap';

    const [dismissed, setDismissed] = useState(() => {
        if (!eligible) return false;
        try {
            return localStorage.getItem(cmsDismissKey(assetId, cmsDetected, state)) === 'true';
        } catch {
            return false;
        }
    });

    useEffect(() => {
        if (!eligible) return;
        try {
            setDismissed(localStorage.getItem(cmsDismissKey(assetId, cmsDetected, state)) === 'true');
        } catch { /* private mode */ }
    }, [eligible, assetId, cmsDetected, state]);

    const visible = eligible && !dismissed;
    if (!visible) return null;

    const hostList = cmsDetected.split(',').filter(Boolean);
    const hostCount = hostList.length;
    const hostLabel = hostCount === 1
        ? hostList[0]
        : `${hostCount} hosts`;

    // 'clean' = positive coverage report (border-color partial/amber but icon = check)
    // 'gap' = real coverage gap (severe styling, CTA to run deep scan)
    const band = state === 'gap' ? 'severe' : 'partial';

    const headline = state === 'clean'
        ? `CMS detected on ${hostLabel} — scanned with ${cmsTemplatesRun} templates, no vulnerabilities found`
        : `CMS detected on ${hostLabel} — CMS-specific templates did not run this scan`;

    const body = state === 'clean'
        ? `We detected a content management system (e.g., WordPress, Drupal, Joomla) and ran ${cmsTemplatesRun} CMS-specific vulnerability templates against the canonical attack-surface paths. No CMS-specific issues were found in this scan.`
        : `We detected a content management system but the CMS-specific vulnerability templates did not execute against the canonical attack-surface paths (e.g., /wp-login.php, /wp-json/wp/v2/users) during this scan. Run a Deep Scan to test the full CMS template library against this asset.`;

    const handleDismiss = () => {
        try {
            localStorage.setItem(cmsDismissKey(assetId, cmsDetected, state), 'true');
        } catch { /* ignore */ }
        setDismissed(true);
    };

    return (
        <div
            className={`cvb-banner cvb-banner--${band}`}
            role="region"
            aria-labelledby="cvb-title-cms"
            data-banner-type="cms-coverage"
            data-cms-state={state}
        >
            <button
                type="button"
                className="cvb-dismiss"
                onClick={handleDismiss}
                aria-label="Dismiss CMS coverage notice"
                title="Dismiss"
            >
                &times;
            </button>
            <div className="cvb-icon" aria-hidden="true">{state === 'clean' ? '✓' : 'ⓘ'}</div>
            <div className="cvb-content">
                <h3 id="cvb-title-cms" className="cvb-title">{headline}</h3>
                <p className="cvb-body">{body}</p>
                {state === 'gap' && (
                    <div className="cvb-actions">
                        <a
                            className="cvb-cta-primary"
                            href={`#deep-scan`}
                        >
                            Run Deep Scan
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
}

// ───────────────────── Port-scan timeout variant (PRD-95 Phase C) ─────────────────────

function PortScanTimeoutBanner({ assetId, assetType, lastPortScanTimeout }) {
    // Eligible when asset is IP-scope AND the receiver stamped a timeout
    // timestamp on the asset entity (set in api/asset-result discovery-ip
    // / vuln-scan-ip / deep-security-ip branches). Cleared via empty-string
    // on the next clean IP-scope write, so the banner self-dismisses once
    // PRD-96's Kali fix produces a complete portScanning stage.
    const eligible = (
        typeof assetType === 'string' &&
        (assetType === 'ip' || assetType === 'cidr') &&
        typeof lastPortScanTimeout === 'string' &&
        lastPortScanTimeout.length > 0
    );

    const [dismissed, setDismissed] = useState(() => {
        if (!eligible) return false;
        try {
            return localStorage.getItem(portScanTimeoutDismissKey(assetId, lastPortScanTimeout)) === 'true';
        } catch {
            return false;
        }
    });

    useEffect(() => {
        if (!eligible) return;
        try {
            setDismissed(localStorage.getItem(portScanTimeoutDismissKey(assetId, lastPortScanTimeout)) === 'true');
        } catch { /* private mode */ }
    }, [eligible, assetId, lastPortScanTimeout]);

    const visible = eligible && !dismissed;
    if (!visible) return null;

    const handleDismiss = () => {
        try {
            localStorage.setItem(portScanTimeoutDismissKey(assetId, lastPortScanTimeout), 'true');
        } catch { /* ignore */ }
        setDismissed(true);
    };

    return (
        <div
            className="cvb-banner cvb-banner--severe"
            role="region"
            aria-labelledby="cvb-title-portscan"
            data-banner-type="port-scan-timeout"
        >
            <button
                type="button"
                className="cvb-dismiss"
                onClick={handleDismiss}
                aria-label="Dismiss port-scan timeout notice"
                title="Dismiss"
            >
                &times;
            </button>
            <div className="cvb-icon" aria-hidden="true">⚠</div>
            <div className="cvb-content">
                <h3 id="cvb-title-portscan" className="cvb-title">
                    Port scan didn't complete
                </h3>
                <p className="cvb-body">
                    Port scanning for this IP/CIDR asset didn't finish within the time budget on the last scan. Open ports below may be incomplete. This is a known limitation on large IP ranges and is being addressed.
                </p>
            </div>
        </div>
    );
}

// ───────────────────────── Top-level orchestrator ─────────────────────────

function CoverageBanner({
    assetId,
    urlsTotal, urlsSelected, urlsTruncated, tierCapApplied, currentTier,
    cmsDetected, cmsFindings, cmsTemplatesRun,
    assetType, lastPortScanTimeout,
}) {
    return (
        <>
            <UrlCapBanner
                assetId={assetId}
                urlsTotal={urlsTotal}
                urlsSelected={urlsSelected}
                urlsTruncated={urlsTruncated}
                tierCapApplied={tierCapApplied}
                currentTier={currentTier}
            />
            <CmsCoverageBanner
                assetId={assetId}
                cmsDetected={cmsDetected}
                cmsFindings={cmsFindings}
                cmsTemplatesRun={cmsTemplatesRun}
            />
            <PortScanTimeoutBanner
                assetId={assetId}
                assetType={assetType}
                lastPortScanTimeout={lastPortScanTimeout}
            />
        </>
    );
}

export default CoverageBanner;
